"use client";

import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";

const AbroadViewContext = createContext(false);

let hintPromise: Promise<boolean> | null = null;

/** One tiny uncached fetch per page load, shared by the banner and badges. */
function fetchInIndonesia(): Promise<boolean> {
	hintPromise ??= fetch("/api/jobs/abroad-hint", { cache: "no-store" })
		.then(
			(r): Promise<{ inIndonesia?: unknown } | null> =>
				r.ok ? r.json() : Promise.resolve(null),
		)
		.then((d) => d?.inIndonesia !== false)
		.catch(() => true);
	return hintPromise;
}

/** Test seam: forget the memoised hint. */
export function resetAbroadHintCache(): void {
	hintPromise = null;
}

/** Marks a subtree as the /jobs abroad view. */
export function AbroadViewProvider({
	active,
	children,
}: {
	active: boolean;
	children: ReactNode;
}) {
	return (
		<AbroadViewContext.Provider value={active}>
			{children}
		</AbroadViewContext.Provider>
	);
}

/** True inside the abroad view. */
export function useIsAbroadView(): boolean {
	return useContext(AbroadViewContext);
}

/**
 * Whether the visitor is in Indonesia, per the no-store hint. `null` while
 * pending or when `enabled` is false. Unknown geo resolves to `true`.
 */
export function useAbroadInIndonesia(enabled: boolean): boolean | null {
	const [value, setValue] = useState<boolean | null>(null);
	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		fetchInIndonesia().then((v) => {
			if (!cancelled) setValue(v);
		});
		return () => {
			cancelled = true;
		};
	}, [enabled]);
	return enabled ? value : null;
}

/** AI Apply is hidden in the abroad view unless the visitor is known to be
 * outside Indonesia. Pending and unknown both hide it. */
export function useHideAiApplyForAbroad(): boolean {
	const abroad = useIsAbroadView();
	const inIndonesia = useAbroadInIndonesia(abroad);
	return abroad && inIndonesia !== false;
}
